/**
 * Validate and shape a new comedy night for insert into `venues`.
 * Weekday names match `DAY_OF_WEEK_MAP` in dateGeneration.js.
 * @see docs/APP_UI_SPEC.md — F4
 */

export const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/** Paths already claimed by the router. A venue slug must not reuse them. */
export const RESERVED_VENUE_SLUGS = [
  'apply',
  'for-venues',
  'legacy',
  'admin',
  'admin_comics',
  'admin_dates',
  'admin_rota',
  'admin_inbox',
  'admin_clubs',
];

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function slugFromNightName(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function nextDisplayOrder(venues) {
  const orders = (venues || []).map((venue) => Number(venue.display_order) || 0);
  if (orders.length === 0) return 1;
  return Math.max(...orders) + 1;
}

function blankToNull(value) {
  const text = String(value || '').trim();
  return text ? text : null;
}

/**
 * @param {object} input form fields
 * @param {{ displayOrder?: number }} [opts]
 * @returns {{ ok: true, row: object } | { ok: false, error: string }}
 */
export function buildNewNightRecord(input, opts = {}) {
  const source = input || {};
  const venueName = String(source.venue_name || '').trim();
  const venueSlug = String(source.venue_slug || '').trim().toLowerCase();
  const dayOfWeek = String(source.day_of_week || '').trim();
  const showTime = String(source.show_time || '').trim();
  const nextDay = String(source.new_day_of_week || '').trim();
  const changeDate = String(source.day_change_date || '').trim();
  const startDate = String(source.start_date || '').trim();
  const link = String(source.google_maps_url || '').trim();

  if (!venueName) return { ok: false, error: 'Name is required.' };
  if (!SLUG_PATTERN.test(venueSlug)) {
    return { ok: false, error: 'Slug must be lowercase letters, numbers, and hyphens.' };
  }
  if (RESERVED_VENUE_SLUGS.includes(venueSlug)) {
    return { ok: false, error: `“${venueSlug}” is already used by the app.` };
  }
  if (!WEEKDAYS.includes(dayOfWeek)) return { ok: false, error: 'Pick the day of the week.' };
  if (!showTime) return { ok: false, error: 'Show time is required.' };
  if (nextDay && !WEEKDAYS.includes(nextDay)) return { ok: false, error: 'Pick a real weekday to move to.' };
  if (nextDay && nextDay === dayOfWeek) {
    return { ok: false, error: 'The new day should be different from the current day.' };
  }
  if (nextDay && !changeDate) return { ok: false, error: 'Pick the date the new day starts.' };
  if (!nextDay && changeDate) return { ok: false, error: 'Pick the weekday it moves to.' };
  if (changeDate && !ISO_DATE.test(changeDate)) {
    return { ok: false, error: 'Day-change date must be YYYY-MM-DD.' };
  }
  if (startDate && !ISO_DATE.test(startDate)) {
    return { ok: false, error: 'First date must be YYYY-MM-DD.' };
  }
  if (link && !/^https?:\/\//i.test(link)) {
    return { ok: false, error: 'The link must start with http:// or https://.' };
  }

  return {
    ok: true,
    row: {
      venue_name: venueName,
      venue_slug: venueSlug,
      venue_location: blankToNull(source.venue_location),
      description: blankToNull(source.description),
      day_of_week: dayOfWeek,
      show_time: showTime,
      doors_time: blankToNull(source.doors_time),
      show_end_time: blankToNull(source.show_end_time),
      start_date: startDate || null,
      new_day_of_week: nextDay || null,
      day_change_date: nextDay ? changeDate : null,
      address: link ? blankToNull(source.address) : null,
      google_maps_url: link || null,
      has_spot_lengths: source.has_spot_lengths !== false,
      display_order: Number.isFinite(opts.displayOrder) ? opts.displayOrder : 0,
    },
  };
}
